// JANUS · DECISION — the most recent open (decided = false) row of jnus_decisions, the table JnusTool reads/writes.
// JnusTool stores no deadline and no per-criterion "scored" flag (matrix scores always hold a value), so neither is shown:
// only the criteria/options the user actually saved into choice_tree.matrix are counted.
import { supabase } from '../../../../lib/supabase';
import type { OrbWidgetModel, WidgetCtx } from '../types';
import { emptyModel, useLoad } from './util';

const BASE = { id: 'janus', toolId: 'jnus', orderFunction: 'execute', title: 'DECISION', deityName: 'JANUS', agentId: 'janus' } as const;

interface Open {
  id: string;
  title: string;
  criteria: number | null;
  options: number | null;
}

export function useJanusWidget(c: WidgetCtx): OrbWidgetModel {
  const { value: open } = useLoad<Open | null>(
    c.userId
      ? async () => {
          const { data, error } = await supabase
            .from('jnus_decisions')
            .select('id, title, choice_tree, created_at')
            .eq('user_id', c.userId!)
            .eq('decided', false)
            .order('created_at', { ascending: false })
            .limit(1);
          if (error) throw error;
          const r = (data ?? [])[0] as { id: string; title: string | null; choice_tree: any } | undefined;
          if (!r || !r.title) return null;
          const m = r.choice_tree?.matrix;
          const crit = Array.isArray(m?.criteria) ? m.criteria.length : null;
          const opts = Array.isArray(m?.options) ? m.options.length : Array.isArray(r.choice_tree?.options) ? r.choice_tree.options.length : null;
          return { id: r.id, title: r.title, criteria: crit, options: opts };
        }
      : null,
    [c.userId, c.tick],
  );

  if (!open) return emptyModel(BASE, c, 'No open decision', { label: 'Decide', run: () => c.openTool('jnus'), tone: 'gold' });

  const lines = [open.title, 'Status: unresolved'];
  if (open.criteria != null) lines.push(`${open.criteria} criteria`);
  if (open.options != null) lines.push(`${open.options} option${open.options === 1 ? '' : 's'}`);
  const tail = open.criteria != null ? ` · ${open.criteria} criteria` : '';
  const raw = `${open.title}${tail}`;
  const summary = raw.length > 40 ? `${open.title.slice(0, Math.max(1, 39 - tail.length)).trimEnd()}…${tail}` : raw;

  return {
    ...BASE,
    status: 'active',
    summary: summary.length > 40 ? `${raw.slice(0, 39).trimEnd()}…` : summary,
    lines,
    primaryAction: { label: 'Decide', run: () => c.openTool('jnus'), tone: 'gold' },
    secondaryActions: [],
    signals: { active: true, related: false },
  };
}
